import React, { useEffect, useState } from "react";
import DebounceSearch from "../../components/common/DebounceSearch";
import { DownArrow } from "../../assets/svg";
import Toggles from "../../components/common/Toggles";
import Action from "../../components/common/Action";
import AddEditFAQ from "./AddEditFAQ";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useDispatch, useSelector } from "react-redux";
import {
  DeleteFAQ,
  DeletefaqQA,
  getFAQList,
  updatefaqQAStatus,
  updateFAQStatus,
} from "../../redux/masterSetting/slice";
import RippleLoader from "../../components/loader/RippleLoader";
import DataNotFound from "../../components/common/DataNotFound";

const FaqSetting = () => {
  const [searchValue, setSearchValue] = useState("");
  const [openTopics, setOpenTopics] = useState({});
  const [openQuestions, setOpenQuestions] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isType, setIsType] = useState("");
  const [modalType, setModalType] = useState("");
  const [selectedFAQId, setSelectedFAQId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteType, setDeleteType] = useState("");
  const [faqDelete, setFaqDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  // Selector
  const { faqListData, loading } = useSelector((store) => ({
    faqListData: store?.settingData?.getFAQListStatus?.data,
    loading: store?.settingData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getFAQList({
        search: searchValue,
      })
    );
  }, [searchValue]);

  // Toggle topic-level visibility
  const toggleTopic = (id) => {
    setOpenTopics((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleQuestion = (questionId) => {
    setOpenQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Update status topic
  const updateStatus = (topic, status, isActive) => {
    dispatch(
      updateFAQStatus({
        id: topic?.id,
        data: {
          status: status,
          is_active: isActive,
        },
      })
    ).then((res) => {
      if (res.type === "updateFAQStatus/fulfilled") {
        dispatch(
          getFAQList({
            search: searchValue,
          })
        );
      }
    });
  };

  // Update QA status
  const updateQAStatus = (topic, status, isActive) => {
    dispatch(
      updatefaqQAStatus({
        id: topic?.id,
        data: {
          status: status,
          is_active: isActive,
        },
      })
    ).then((res) => {
      if (res.type === "updatefaqQAStatus/fulfilled") {
        dispatch(
          getFAQList({
            search: searchValue,
          })
        );
      }
    });
  };

  const handleToggle = (item) => {
    const newStatus = item.status === "active" ? "inactive" : "active";
    const isActive = newStatus === "active";

    if (item?.questions) {
      // It's a topic
      updateStatus(item, newStatus, isActive);
    } else {
      // It's a question
      updateQAStatus(item, newStatus, isActive);
    }
  };

  const handleDeleteClick = (type, id) => {
    setConfirmDelete(true);
    setDeleteType(type);
    setFaqDelete(id);
  };

  const handleConfirmDelete = () => {
    if (deleteType === "topic") {
      deleteFAQ();
    } else {
      deleteFaqQA();
    }
  };

  // Delete FAQ Topic
  const deleteFAQ = () => {
    setIsLoading(true);

    dispatch(DeleteFAQ(faqDelete))
      .then((res) => {
        if (res.type === "DeleteFAQ/fulfilled") {
          dispatch(getFAQList({ search: searchValue }));
        }
        setIsLoading(false);
        setConfirmDelete(false);
      })
      .catch(() => {
        setIsLoading(false);
        setConfirmDelete(false);
      });
  };

  // Delete Q&A
  const deleteFaqQA = () => {
    setIsLoading(true);
    dispatch(DeletefaqQA(faqDelete))
      .then((res) => {
        if (res.type === "DeletefaqQA/fulfilled") {
          dispatch(getFAQList({ search: searchValue }));
        }
        setIsLoading(false);
        setConfirmDelete(false);
      })
      .catch(() => {
        setIsLoading(false);
        setConfirmDelete(false);
      });
  };

  return (
    <>
      <div className="space-y-5">
        {/* Search and Add Topic Section */}
        <div className="flex items-center space-x-4 w-full">
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <button
            className="bg-themeBtn text-white flex items-center justify-center px-2 py-[7px] rounded uppercase font-Proxima text-base w-40"
            type="button"
            onClick={() => {
              setIsModalOpen(true);
              setIsType("add");
              setModalType("topic");
              setSelectedFAQId(null);
            }}
          >
            + Add Topic
          </button>
        </div>
        {loading ? (
          <RippleLoader />
        ) : faqListData?.data?.length > 0 ? (
          <div className="bg-white rounded-lg border border-borderDot p-4">
            {/* Render FAQ topics */}
            {(faqListData?.data || []).map((topic) => (
              <div key={topic.id} className="mb-6">
                {/* Topic Header */}
                <div
                  className={`p-4 rounded-t-lg hover:bg-lightprimaryorange ${
                    openTopics[topic.id]
                      ? "bg-secondaryOrange"
                      : "bg-primaryGray"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div className="text-base font-normal font-Proxima flex gap-2">
                      <p className="text-black">Topic:</p>
                      <p className="text-textSecondary">
                        {topic?.topic_title
                          ? topic?.topic_title
                              .toLowerCase()
                              .replace(/\b\w/g, (char) => char.toUpperCase())
                          : "-"}
                      </p>
                    </div>
                    <div className="text-sm font-normal flex items-center gap-4 text-mgrey font-Proxima">
                      <Toggles
                        checked={topic?.status === "active"}
                        toggleFor={topic?.id}
                        onChange={() => handleToggle(topic)}
                      />
                      <Action
                        optionList={
                          <>
                            <li
                              className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                              onClick={() => {
                                setIsModalOpen(true);
                                setIsType("edit");
                                setModalType("topic");
                                setSelectedFAQId(topic?.id);
                              }}
                            >
                              View/Edit
                            </li>
                            <li
                              className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                              onClick={() => {
                                setIsModalOpen(true);
                                setModalType("question");
                                setIsType("add");
                                setSelectedFAQId({ topicId: topic?.id });
                              }}
                            >
                              Add Q&A
                            </li>
                            <li
                              className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                              onClick={() =>
                                handleDeleteClick("topic", topic?.id)
                              }
                            >
                              Delete
                            </li>
                          </>
                        }
                      />
                      <button onClick={() => toggleTopic(topic.id)}>
                        <DownArrow
                          className={`transform transition-transform duration-300 ${
                            openTopics[topic.id] ? "rotate-180" : "rotate-0"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
                {/* Expandable Questions Section */}
                {openTopics[topic.id] && (
                  <div className="mt-2 space-y-4">
                    {topic.questions.map((question) => (
                      <div
                        key={question?.id}
                        className="bg-lightprimaryorange py-6 px-8"
                      >
                        <div className="flex justify-between items-center gap-3">
                          <p className="text-base text-primaryBlack font-normal font-Proxima ">
                            Question:{" "}
                            {question?.question
                              ? question?.question
                                  .toLowerCase()
                                  .replace(/^./, (char) => char.toUpperCase())
                              : "-"}
                          </p>
                          <div className="text-sm font-normal flex items-center gap-4 text-mgrey font-Proxima">
                            <Toggles
                              checked={question?.status === "active"}
                              toggleFor={question?.id}
                              onChange={() => handleToggle(question)}
                            />
                            <Action
                              optionList={
                                <>
                                  <li
                                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                    onClick={() => {
                                      setIsModalOpen(true);
                                      setIsType("edit");
                                      setModalType("question");
                                      setSelectedFAQId({
                                        questionId: question?.id,
                                        topicId: topic?.id,
                                      });
                                    }}
                                  >
                                    Edit
                                  </li>
                                  <li
                                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                    onClick={() =>
                                      handleDeleteClick(
                                        "question",
                                        question?.id
                                      )
                                    }
                                  >
                                    Delete
                                  </li>
                                </>
                              }
                            />
                            <button onClick={() => toggleQuestion(question.id)}>
                              <DownArrow
                                className={`transform transition-transform duration-300 ${
                                  openQuestions[question.id]
                                    ? "rotate-180"
                                    : "rotate-0"
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                        {openQuestions[question.id] && (
                          <div className="flex justify-between items-center">
                            {question.answer.map((ans, index) => (
                              <p
                                key={index}
                                className="text-base text-primaryBlack mt-2 w-[90%]"
                              >
                                Answer:{" "}
                                {ans?.answer
                                  ? ans?.answer
                                      .toLowerCase()
                                      .replace(/^./, (char) =>
                                        char.toUpperCase()
                                      )
                                  : "-"}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <DataNotFound />
        )}
      </div>
      {/* Confirmation Modal */}
      {confirmDelete && (
        <ConfirmationModel
          confirmText={"Delete"}
          subText={
            deleteType === "topic" ? "All Q&A tagged will also be deleted" : ""
          }
          onClose={() => setConfirmDelete(false)}
          onConfirm={handleConfirmDelete}
          isLoading={isLoading}
        />
      )}
      {isModalOpen && (
        <AddEditFAQ
          onClose={closeModal}
          faqListData={faqListData}
          isType={isType}
          selectedFAQId={selectedFAQId}
          modalType={modalType}
          searchValue={searchValue}
        />
      )}
    </>
  );
};

export default FaqSetting;
